import React, { useEffect } from 'react'
import { createStyles, withStyles } from '@paintscout/ui'
import type { WithStyles } from '@paintscout/ui'
import { DropdownSelect, withClientOptions, withUser } from '@paintscout/ui-business'
import type { DropdownSelectOption, MultiSelectProps, WithClientOptions, WithUser } from '@paintscout/ui-business'
import { getFeature } from '@paintscout/util/builder'
import { isEmailValid } from '@paintscout/util/util'

const styles = () => createStyles({})

export interface AddressBookSelectProps
  extends Omit<MultiSelectProps, 'classes' | 'options' | 'variant'>,
    WithClientOptions,
    WithUser,
    WithStyles<typeof styles> {
  bouncedValues?: DropdownSelectOption[]
  options?: DropdownSelectOption[]
}

const AddressBookSelect: React.FC<AddressBookSelectProps> = (props) => {
  const { options = [], clientOptions, user, bouncedValues, onChange, ...selectProps } = props
  const [initialOptions, setInitialOptions] = React.useState<DropdownSelectOption[]>(props.value)

  useEffect(() => {
    if (bouncedValues && !JSON.stringify(props.value).includes(JSON.stringify(bouncedValues))) {
      onChange(bouncedValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const companyAddresses = clientOptions.options.options.emails.addressBook
  const personalAddresses = user.preferences.addressBook
  const hasAddressBook = getFeature({ options: clientOptions.options, path: 'quotes.emails.hasAddressBook' })

  const groupedOptions: DropdownSelectOption[] = [
    ...options,
    ...initialOptions,
    hasAddressBook
      ? {
          label: 'Personal Address Book',
          options: personalAddresses.map((address) => ({ value: address, label: address }))
        }
      : null,
    hasAddressBook
      ? {
          label: 'Company Address Book',
          options: companyAddresses.map((address) => ({ value: address, label: address }))
        }
      : null
  ].filter(Boolean) as DropdownSelectOption[]

  const handleToChange = (values: DropdownSelectOption[]) => {
    if (onChange) {
      onChange(values)
    }
  }

  const invalidEmailMessage = (value: string) => {
    if (!isEmailValid(value)) {
      return 'Please enter a valid email address'
    }
  }

  return (
    <DropdownSelect
      {...selectProps}
      variant="multi"
      options={groupedOptions}
      noOptionsMessage={hasAddressBook ? 'Address Book and related emails will show here' : undefined}
      onChange={handleToChange}
      onCreateOption={(value) => {
        // createLabel already trims for display, so trim here too — an untrimmed address
        // passes isEmailValid and then reaches the mutation with the whitespace intact.
        const address = value.trim()
        setInitialOptions([...initialOptions, { value: address, label: address }])
        handleToChange([...props.value, { value: address, label: address }])
      }}
      validateCreatedOption={invalidEmailMessage}
      createLabel={(inputValue) => `Use "${inputValue.trim()}"`}
    />
  )
}

export default withStyles(styles)(withClientOptions(withUser(AddressBookSelect)))
